/**
 * Capstone Project Service
 * API integration for Capstone Project Framework
 */

import api from './api';
import {
  CapstoneProject,
  CapstoneModule,
  CapstoneDeliverable,
  CapstoneTemplate,
  USCISUseCase,
  CapstoneAnalytics,
  UserCapstoneProgress
} from '../types/capstone';

const BASE_URL = '/capstone';

// Project CRUD operations
export const capstoneService = {
  // Projects
  async createProject(projectData: Partial<CapstoneProject>): Promise<CapstoneProject> {
    const response = await api.post(`${BASE_URL}/projects`, projectData);
    return response.data.data;
  },

  async getProjects(): Promise<CapstoneProject[]> {
    const response = await api.get(`${BASE_URL}/projects`);
    return response.data.data || [];
  },

  async getProjectById(projectId: string): Promise<CapstoneProject> {
    const response = await api.get(`${BASE_URL}/projects/${projectId}`);
    return response.data.data;
  },

  async updateProject(projectId: string, updates: Partial<CapstoneProject>): Promise<CapstoneProject> {
    const response = await api.put(`${BASE_URL}/projects/${projectId}`, updates);
    return response.data.data;
  },

  async deleteProject(projectId: string): Promise<void> {
    await api.delete(`${BASE_URL}/projects/${projectId}`);
  },

  // Modules
  async getModules(projectId: string): Promise<CapstoneModule[]> {
    const response = await api.get(`${BASE_URL}/projects/${projectId}/modules`);
    return response.data;
  },

  async updateModule(projectId: string, moduleId: string, updates: Partial<CapstoneModule>): Promise<CapstoneModule> {
    const response = await api.put(`${BASE_URL}/projects/${projectId}/modules/${moduleId}`, updates);
    return response.data;
  },

  async startModule(projectId: string, moduleId: string): Promise<CapstoneModule> {
    const response = await api.post(`${BASE_URL}/projects/${projectId}/modules/${moduleId}/start`);
    return response.data;
  },

  async completeModule(projectId: string, moduleId: string): Promise<CapstoneModule> {
    const response = await api.post(`${BASE_URL}/projects/${projectId}/modules/${moduleId}/complete`);
    return response.data;
  },

  // Deliverables
  async createDeliverable(deliverableData: Partial<CapstoneDeliverable>): Promise<CapstoneDeliverable> {
    const response = await api.post(`${BASE_URL}/deliverables`, deliverableData);
    return response.data;
  },

  async getDeliverable(deliverableId: string): Promise<CapstoneDeliverable> {
    const response = await api.get(`${BASE_URL}/deliverables/${deliverableId}`);
    return response.data;
  },

  async updateDeliverable(deliverableId: string, updates: Partial<CapstoneDeliverable>): Promise<CapstoneDeliverable> {
    const response = await api.put(`${BASE_URL}/deliverables/${deliverableId}`, updates);
    return response.data;
  },

  async submitDeliverable(deliverableId: string): Promise<CapstoneDeliverable> {
    const response = await api.post(`${BASE_URL}/deliverables/${deliverableId}/submit`);
    return response.data;
  },

  async reviewDeliverable(deliverableId: string, reviewData: any): Promise<CapstoneDeliverable> {
    const response = await api.post(`${BASE_URL}/deliverables/${deliverableId}/review`, reviewData);
    return response.data;
  },

  async addComment(deliverableId: string, comment: string): Promise<CapstoneDeliverable> {
    const response = await api.post(`${BASE_URL}/deliverables/${deliverableId}/comments`, { comment });
    return response.data;
  },

  async uploadAttachment(deliverableId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${BASE_URL}/deliverables/${deliverableId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.fileUrl;
  },

  // Templates
  async getTemplates(): Promise<CapstoneTemplate[]> {
    const response = await api.get(`${BASE_URL}/templates`);
    return response.data.data || [];
  },

  async getTemplateById(templateId: string): Promise<CapstoneTemplate> {
    const response = await api.get(`${BASE_URL}/templates/${templateId}`);
    return response.data.data;
  },

  async getTemplatesByModule(moduleNumber: number): Promise<CapstoneTemplate[]> {
    const response = await api.get(`${BASE_URL}/templates?module=${moduleNumber}`);
    return response.data;
  },

  // Use Cases
  async getUseCases(): Promise<USCISUseCase[]> {
    const response = await api.get(`${BASE_URL}/use-cases`);
    return response.data.data || [];
  },

  async getUseCaseById(useCaseId: string): Promise<USCISUseCase> {
    const response = await api.get(`${BASE_URL}/use-cases/${useCaseId}`);
    return response.data.data;
  },

  // Team Management
  async addTeamMember(projectId: string, userId: string): Promise<CapstoneProject> {
    const response = await api.post(`${BASE_URL}/projects/${projectId}/members`, { userId });
    return response.data;
  },

  async removeTeamMember(projectId: string, userId: string): Promise<CapstoneProject> {
    const response = await api.delete(`${BASE_URL}/projects/${projectId}/members/${userId}`);
    return response.data;
  },

  async assignMentor(projectId: string, mentorId: string): Promise<CapstoneProject> {
    const response = await api.put(`${BASE_URL}/projects/${projectId}/mentor`, { mentorId });
    return response.data;
  },

  // Analytics
  async getAnalyticsOverview(): Promise<CapstoneAnalytics> {
    const response = await api.get(`${BASE_URL}/analytics/overview`);
    return response.data;
  },

  async getProjectAnalytics(projectId: string): Promise<any> {
    const response = await api.get(`${BASE_URL}/analytics/projects/${projectId}`);
    return response.data;
  },

  async getUserProgress(userId: string): Promise<UserCapstoneProgress> {
    const response = await api.get(`${BASE_URL}/analytics/user/${userId}`);
    return response.data;
  },

  // Certificates
  async generateCertificate(projectId: string): Promise<string> {
    const response = await api.post(`${BASE_URL}/certificates/generate/${projectId}`);
    return response.data.certificateId;
  },

  async getCertificate(certificateId: string): Promise<any> {
    const response = await api.get(`${BASE_URL}/certificates/${certificateId}`);
    return response.data;
  },

  async verifyCertificate(certificateId: string): Promise<boolean> {
    const response = await api.get(`${BASE_URL}/certificates/verify/${certificateId}`);
    return response.data.isValid;
  }
};

export default capstoneService;
